import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import * as XLSX from "xlsx";
import CircularProgress from "@mui/material/CircularProgress"
import { LoadNewOrganizations } from '../../actions'
import { setBackHandler, setHeaderTitle } from '../../helpers/header.helper'
import { mapToOrganizationDetails, OrganizationDetails, OrganizationModel } from '../../models/organization.model'
import { LandlineSubscriberFormData } from '../../models/add-phone-number.model'
import * as selector from '../../selectors/selectors'
import { AppRoutes } from '../routes/route.path'
import { pageTitles, ToasterTexts } from '../shared/constants'
import { WhiteTickIcon } from '../../icon'
import SelectOrganizationStep, { formatAddressSummary } from './select-organization-step.component'
import HostNumbersResultStep from './host-numbers-result-step.component'
import { EMPTY_IMPORT_ADDRESS, FILE_COLUMNS, ImportEditableRow, importRowsFromFile, normalizeHeader } from './import-numbers-shared.component'
import setToast from '../../helpers/toaster.helper';
import { UtilityService } from '../../services/UtilityService';
import { mapToPhoneNumber, PhoneNumberModel } from '../../models/phone-number.model';
import { PhoneNumberService } from '../../services/PhonenumberService';
import { OrganizationService } from '../../services/OrganizationService';
import { ToasterType } from '../shared/toaster/toaster.model';
import UploadFileStep from './upload-file-step.component';
import ValidationReportStep from './validation-report-step.component';

const MAX_IMPORT_FILE_SIZE_BYTES = 25 * 1024 * 1024;

const getEditedCellValue = (row: ImportEditableRow, header: string): string | undefined => {
    const field = FILE_COLUMNS[normalizeHeader(header)] || header;
    if (field === "Name" || field === "Number") {
        return row[field] || "";
    }
    return undefined;
};

const buildUpdatedUploadedFile = (file: File, originalRows: Record<string, string>[], editedRows: ImportEditableRow[]): File => {
    const headers = Object.keys(originalRows[0] || {});
    const data = editedRows.map((edited, index) => {
        const original = originalRows[edited.rowIndex] ?? originalRows[index] ?? {};
        const next: Record<string, string> = { ...original };
        (headers.length ? headers : Object.keys(next)).forEach((header) => {
            const value = getEditedCellValue(edited, header);
            if (value !== undefined) {
                next[header] = value;
            }
        });
        return next;
    });

    const worksheet = XLSX.utils.json_to_sheet(data, headers.length ? { header: headers } : undefined);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const fileName = file.name.toLowerCase();
    if (fileName.endsWith(".csv")) {
        return new File([XLSX.utils.sheet_to_csv(worksheet)], file.name, { type: "text/csv" });
    }

    const bookType = fileName.endsWith(".xls") && !fileName.endsWith(".xlsx") ? "xls" : "xlsx";
    const buffer = XLSX.write(workbook, { bookType, type: "array" });
    return new File([buffer], file.name, {
        type: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
};

const NewNumberBulkImportComponent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const IsDarkMode: boolean = useSelector(selector.isDarkMode);
    const Organizations: OrganizationModel[] = useSelector(selector.newOrganizations);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const stepContentScrollRef = useRef<HTMLDivElement>(null);

    const [activeStep, setActiveStep] = useState<number>(0);
    const [selectedOrgId, setSelectedOrgId] = useState<string>("");
    const [orgDetails, setOrgDetails] = useState<OrganizationDetails | null>(null);
    const [useOrgAddress, setUseOrgAddress] = useState<boolean>(false);
    const [address, setAddress] = useState<LandlineSubscriberFormData>(EMPTY_IMPORT_ADDRESS);
    const [uploadedFile, setUploadedFile] = useState<File>();
    const [jsonFileData, setJsonFileData] = useState<Record<string, string>[]>([]);
    const [sheetCount, setSheetCount] = useState<number>(0);
    const [fileUploadError, setFileUploadError] = useState<string>("");
    const [importRows, setImportRows] = useState<ImportEditableRow[]>([]);
    const [errorRows, setErrorRows] = useState<ImportEditableRow[]>([]);
    const [existingPhoneNumbers, setExistingPhoneNumbers] = useState<PhoneNumberModel[]>([]);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [createCount, setCreateCount] = useState<number>(0);
    const [importSubmitted, setImportSubmitted] = useState<boolean>(false);

    const Steps = [
        { label: "Batch details", description: "Organization and address" },
        { label: "Upload file", description: "Name and Phone Number" },
        { label: "Validation report", description: "Failed rows and reasons" },
        { label: "Host batch", description: "Runs in the background" }
    ];

    const applyOrgAddress = (details: OrganizationDetails | null, current: LandlineSubscriberFormData) => ({
        ...current,
        HouseNumber: details?.HouseNumber || "",
        Address: details?.Address || "",
        ZipCode: details?.ZipCode || "",
        City: details?.City || "",
        State: details?.State || "",
        Country: details?.Country || ""
    });

    const handleChangeOrg = (organizationId: string) => {
        setSelectedOrgId(organizationId);
        setUseOrgAddress(false);
        setOrgDetails(null);
        setExistingPhoneNumbers([]);
        setAddress({
            ...EMPTY_IMPORT_ADDRESS,
            FirstName: address.FirstName,
            LastName: address.LastName
        });

        if (!organizationId) return;

        OrganizationService.getOrganizationById(organizationId)
            .then((res) => {
                setOrgDetails(mapToOrganizationDetails(res.data));
            })
            .catch(() => { });

        PhoneNumberService.getPhoneNumbersByOrgId(organizationId)
            .then((res) => {
                if (res.status === 200) {
                    setExistingPhoneNumbers(res.data.map(mapToPhoneNumber));
                }
            })
            .catch(() => { });
    };

    const handleToggleUseOrgAddress = (checked: boolean) => {
        setUseOrgAddress(checked);
        if (checked) {
            setAddress(applyOrgAddress(orgDetails, address));
        } else {
            setAddress({
                ...address,
                HouseNumber: "",
                Address: "",
                ZipCode: "",
                City: "",
                State: "",
                Country: ""
            });
        }
    };

    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleClearFile = () => {
        setUploadedFile(undefined);
        setJsonFileData([]);
        setSheetCount(0);
        setFileUploadError("");
        setImportRows([]);
        resetFileInput();
    };

    const getRowErrors = useCallback((rows: ImportEditableRow[]): ImportEditableRow[] => {
        const existingNumbers = new Set(
            existingPhoneNumbers.map((phone) => UtilityService.unmaskPhoneNumberWithPlus(phone.PhoneNumber) || phone.PhoneNumber)
        );
        const seenInFile = new Set<string>();
        return rows.map((row) => {
            const name = (row.Name || "").trim();
            const digits = (row.Number || "").replace(/\D/g, "");
            const phoneNumber = UtilityService.unmaskPhoneNumberWithPlus(row.Number);
            let phoneError = "";

            if (!digits) {
                phoneError = "Phone Number is required";
            } else if (digits.length > 15 || !UtilityService.validPhoneNumber(row.Number)) {
                phoneError = "Invalid phone number";
            } else if (existingNumbers.has(phoneNumber)) {
                phoneError = "Phone number already exists";
            } else if (seenInFile.has(phoneNumber)) {
                phoneError = "Duplicate phone number in file";
            } else {
                seenInFile.add(phoneNumber);
            }

            return {
                RowId: row.RowId,
                rowIndex: row.rowIndex,
                Name: name.length === 0 ? "Name is required" : "",
                Number: phoneError
            };
        });
    }, [existingPhoneNumbers]);

    const handleValidateRows = () => {
        if (!uploadedFile || !selectedOrgId) return;
        const rows = importRowsFromFile(jsonFileData);
        setImportRows(rows);
        setErrorRows(getRowErrors(rows));
        setActiveStep(2);
    };

    const handleRowsChange = useCallback((rows: ImportEditableRow[]) => {
        setImportRows(rows);
        setErrorRows(getRowErrors(rows));
    }, [getRowErrors]);

    const handleHostNumbers = (summary: { created: number; skipped: number; failed: number }) => {
        if (!uploadedFile || isCreating) return;

        const updatedFile = buildUpdatedUploadedFile(uploadedFile, jsonFileData, importRows);

        setCreateCount(summary.created);
        setIsCreating(true);

        const formData = new FormData();
        formData.append("file", updatedFile);
        const loaPersonName = `${address.FirstName || ""} ${address.LastName || ""}`.trim() || orgDetails?.Name || "";
        const addressData = {
            loaPersonName,
            houseNumber: address.HouseNumber || "",
            streetName: address.Address || "",
            city: address.City || "",
            stateCode: address.State || "",
            zip: address.ZipCode || "",
            county: address.Country || "",
        };
        
        formData.append("address", JSON.stringify(addressData));


        PhoneNumberService.createPhoneNumbersBulk(formData, selectedOrgId)
            .then((res) => {
                if (res.status === 200) {
                    setImportSubmitted(true);
                    setActiveStep(3);
                } else {
                    setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER);
                }
                setIsCreating(false);
            })
            .catch(() => {
                setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER);
                setIsCreating(false);
            });

    };

    const handleUploadAnotherFile = () => {
        handleClearFile();
        setErrorRows([]);
        setImportSubmitted(false);
        setCreateCount(0);
        setActiveStep(1);
    };

    const handleFileUpload = (file: File | undefined) => {
        if (!file) return;

        if (file.size > MAX_IMPORT_FILE_SIZE_BYTES) {
            setFileUploadError("File size should not exceed 25 MB");
            return;
        }

        setFileUploadError("");

        const reader = new FileReader();
        const isCSV = file.name.toLowerCase().endsWith(".csv");
        if (isCSV) {
            reader.readAsText(file);
        } else {
            reader.readAsArrayBuffer(file);
        }

        reader.onload = (event: any) => {
            let workbook;

            if (isCSV) {
                workbook = XLSX.read(event.target.result, { type: "string", cellText: true });
            } else {
                const data = new Uint8Array(event.target.result);
                workbook = XLSX.read(data, { type: "array", cellText: true });
            }

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json<Record<string, string>>(worksheet, {
                defval: "",
                raw: false
            });

            if (!jsonData || jsonData.length === 0) {
                setFileUploadError("file contains no data.");
                return;
            }

            if (jsonData.length >= 20000) {
                setFileUploadError("file contains more then 20000 rows");
                return;
            }

            const hasValidRow = jsonData.some((row) =>
                Object.values(row).some((cell) => cell?.toString().trim() !== "")
            );

            if (!hasValidRow) {
                setFileUploadError("At least one row with data is required.");
                return;
            }

            setUploadedFile(file);
            setJsonFileData(jsonData);
            setSheetCount(workbook.SheetNames?.length || 1);
        };
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <SelectOrganizationStep
                        Organizations={Organizations}
                        SelectedOrgId={selectedOrgId}
                        OrgDetails={orgDetails}
                        Address={address}
                        UseOrgAddress={useOrgAddress}
                        IsDarkMode={IsDarkMode}
                        OnChangeOrg={handleChangeOrg}
                        OnChangeAddress={setAddress}
                        OnToggleUseOrgAddress={handleToggleUseOrgAddress}
                        OnCancel={() => navigate(AppRoutes.NEW_PhONE_NUMBER)}
                        OnContinue={() => {
                            if (selectedOrgId) {
                                setActiveStep(1);
                            }
                        }}
                    />
                );
            case 1:
                return (
                    <UploadFileStep
                        IsDarkMode={IsDarkMode}
                        UploadedFile={uploadedFile}
                        RowCount={jsonFileData.length}
                        SheetCount={sheetCount}
                        FileUploadError={fileUploadError}
                        FileInputRef={fileInputRef}
                        OrganizationName={Organizations.find((org) => org.ID === selectedOrgId)?.Name || orgDetails?.Name || ""}
                        AddressSummary={useOrgAddress ? "Same as organization" : (formatAddressSummary(address) || "")}
                        OnFileUpload={handleFileUpload}
                        OnClearFile={handleClearFile}
                        OnBack={() => setActiveStep(0)}
                        OnValidate={handleValidateRows}
                    />
                );
            case 2:
                return (
                    <ValidationReportStep
                        IsDarkMode={IsDarkMode}
                        RowCount={jsonFileData.length}
                        Rows={importRows}
                        ErrorRows={errorRows}
                        OnRowsChange={handleRowsChange}
                        OnBack={() => setActiveStep(1)}
                        OnHost={handleHostNumbers}
                        GetRowErrors={getRowErrors}
                    />
                );
            case 3:
                return importSubmitted ? (
                    <HostNumbersResultStep
                        IsDarkMode={IsDarkMode}
                        FileName={uploadedFile?.name}
                        OnViewReports={() => navigate(AppRoutes.NEW_PHONE_NUMBER_IMPORT_REPORTS, { state: { OrganizationID: selectedOrgId } })}
                        OnUploadAnother={handleUploadAnotherFile}
                    />
                ) : null;
            default:
                return null;
        }
    };

    useEffect(() => {
        setHeaderTitle(pageTitles.BULK_ADD_NUMBERS);
        setBackHandler(() => navigate(AppRoutes.NEW_PhONE_NUMBER));
        if (!Organizations?.length) {
            LoadNewOrganizations(dispatch);
        }

        return () => {
            setBackHandler();
        };
    }, []);

    useEffect(() => {
        const scrollContainer = stepContentScrollRef.current;
        if (!scrollContainer) return;

        scrollContainer.scrollTop = 0;
        requestAnimationFrame(() => {
            scrollContainer.scrollTop = 0;
        });
    }, [activeStep]);

    useEffect(() => {
        if (useOrgAddress && orgDetails) {
            setAddress((current) => applyOrgAddress(orgDetails, current));
        }
    }, [orgDetails, useOrgAddress]);

    return (
        <div ref={stepContentScrollRef} className={`w-p-98 mx-auto h-p-98 o-y-auto ${IsDarkMode ? "bg-color-primary-background" : "bg-color-report-background"} border-primary-divider`}>
            <div className='w-p-95 mx-auto pb-2'>
                <div className='d-flex align-center w-p-100'>
                    <div className={`w-p-100 mx-auto br-5 py-2 bg-color-secondary border-primary-divider`}>
                        <div className='d-flex align-center w-p-95 mx-auto py-1'>
                            {Steps.map((step, index) => {
                                const currentStep = isCreating ? 3 : activeStep;
                                const isCompleted = index < currentStep;
                                const isHighlighted = index <= currentStep;
                                return (
                                    <React.Fragment key={step.label}>
                                        <div className='d-flex align-center'>
                                            <div
                                                className={`d-flex align-center justify-center h4-medium br-50 ${isHighlighted ? "bg-color-primary-btn color-secondary" : "color-text-field-label"}`}
                                                style={{
                                                    width: 32,
                                                    height: 32,
                                                    minWidth: 32,
                                                    backgroundColor: isHighlighted ? undefined : "#EEF2F4"
                                                }}
                                            >
                                                {isCompleted ? <WhiteTickIcon /> : index + 1}
                                            </div>
                                            <div className='d-flex flex-direction ml-1'>
                                                <div className={`h4-medium w-space ${isHighlighted ? "color-primary-accent" : "color-text-field-label"}`}>
                                                    {step.label}
                                                </div>
                                                <div className='h5-regular color-menu-list-font-subnav w-space'>
                                                    {step.description}
                                                </div>
                                            </div>
                                        </div>
                                        {index < Steps.length - 1 && (
                                            <div className='flex-1 mx-2' style={{ height: 1, backgroundColor: "#E6E6E6" }} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                </div>
                {isCreating ? (
                    <div className={`w-p-100 mt-2 d-flex flex-direction align-center justify-center ${IsDarkMode ? "bg-color-primary-background" : "bg-color-secondary"}`} style={{ minHeight: 420 }}>
                        <CircularProgress size={40} thickness={4} sx={{ color: "#1450FF" }} />
                        <div className="h3-medium color-primary-accent mt-2" style={{ fontFamily: "silkasemibold" }}>
                            Hosting {createCount} {createCount === 1 ? "number" : "numbers"}
                        </div>
                    </div>
                ) : (
                    <div className='d-flex mx-auto flex-direction'>
                        {renderStepContent(activeStep)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NewNumberBulkImportComponent;
